import React, { useCallback, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { ListingData } from '@nykaa/listing-shared/search/store/types';
import { changePage as loadMore } from '@nykaa/listing-shared/search/store/actions';
import styled from 'App/styled';
import { State } from 'store/types';
import { ListingProductLoader } from 'components/ListingLoader';
import Spinner from 'components/ListingLoader/spinner';
import ProductList from 'components/ProductList';
import InfiniteLoader from 'components/InfiniteLoader';
import {
  INFINITE_SCROLL_PAGE_THRESHOLD,
  LOAD_MORE_BUTTON_TEXT,
  NO_PRODUCT_MESSAGE,
  PRODUCT_LOADER_COUNT,
  PRODUCT_COUNT,
} from 'constants/product';
import { getStoreName, forNextPage } from '../mapItems';
import PaginationByNumber from './Pagination';
import Scroll from '../Scroll';
import { pushData } from '@nykaa/data-layer';
import { getCookie } from '@nykaa/utils/cookies';
import { PageJourney } from '@nykaa/data-layer/store/action';
import { getEligibleForVariant } from 'helpers/experimentV2';
import { PLP_INLINE_WIDGETS_DWEB } from 'constants/experiments';
import { deviceTypes } from 'shared/constants';
import { DWEB } from 'constants/devices';
import { fetchInlineWidgets } from '@nykaa/listing-shared/category/store/action';
import { logger } from '@nykaa/logger';

const LoadMore = styled.div`
  display: flex;
  justify-content: center;
  padding: 20px 0;

  .load-more-button {
    border-radius: 3px;
    padding: 12px 45px;
    border: none;
    font-weight: 700;
    text-transform: uppercase;
    color: #fff;
    background: rgba(3, 2, 26, 0.6);
    text-decoration: none;
    line-height: 20px;
    cursor: pointer;
  }
`;
const NoProduct = styled.p`
  display: flex;
  justify-content: center;
  padding: 20px 0;
`;

interface Props {
  isSearch: boolean;
  journeyData?: PageJourney;
  tagsOnListingEnabled?: boolean;
  tagsV2Enabled?: boolean;
}

const View = ({
  isSearch,
  journeyData,
  tagsOnListingEnabled,
  tagsV2Enabled,
}: Props) => {
  const dispatch = useDispatch();
  const storeName = getStoreName(isSearch);
  const listingPage = useSelector(
    (state: State) => state[storeName],
    shallowEqual
  );
  const changePage = useCallback(
    (params) => {
      dispatch(forNextPage(isSearch)(params));
    },
    [dispatch, isSearch]
  );
  const errorFetchingPage = listingPage?.errorFetchingPage;
  const isLoading = listingPage?.loading;
  const isPageLoading = listingPage?.loadingPage;
  /* eslint-disable react-hooks/exhaustive-deps */
  const products = listingPage?.listingData?.products || [];
  const pageNumber = listingPage?.pageNumber;
  const stopFurtherCall = !!listingPage?.listingData?.stopFurtherCall;
  const totalFound = listingPage?.listingData?.totalFound || 0;
  const offset = (listingPage?.listingData as ListingData)?.offset || 0;
  const productCount = products?.length || 0;
  const remainingProduct = totalFound - offset - productCount;
  const totalPages = Math.ceil(totalFound / PRODUCT_COUNT);
  const [hasInteracted, setInteraction] = useState(false);
  const [isInlineWidgetLoaded, setInlineWidgetsLoaded] = useState(false);

  const inlineWidgetConfig = listingPage?.listingData?.inlineWidgetConfig;
  const inlineWidgetEnabled = useSelector((state: State) =>
    getEligibleForVariant(state, PLP_INLINE_WIDGETS_DWEB, 'A')
  );

  /**
   * Fetches inline widgets for product listing page when:
   * 1. The feature is enabled for the user (experiment eligibility)
   * 2. Widget configuration is available
   * 3. Widgets haven't been loaded yet in this session
   */
  useEffect(() => {
    if (
      !isSearch &&
      inlineWidgetEnabled &&
      inlineWidgetConfig &&
      !isInlineWidgetLoaded
    ) {
      const otherParams = {
        device: deviceTypes.DESKTOP,
        platform: DWEB,
      };
      try {
        fetchInlineWidgets({
          params: { ...inlineWidgetConfig },
          otherParams,
          store: __PLATFORM__,
          isDesktop: true,
        })(dispatch);
      } catch (error) {
        logger.error(error, 'Error in fetching inline widgets');
      }
      setInlineWidgetsLoaded(true);
    }
  }, [dispatch, inlineWidgetConfig, inlineWidgetEnabled, isInlineWidgetLoaded]);

  useEffect(() => {
    pushData({
      listingProducts: products,
      pinCode: getCookie('pinCodeDel').toString() || '',
    });
  }, [products]);

  if (isLoading) {
    return (
      <ListingProductLoader length={productCount || PRODUCT_LOADER_COUNT} />
    );
  }

  const noProducts = !(products && products.length >= 1);
  const loadMoreClick = () => {
    // e.preventDefault();
    if (!stopFurtherCall) {
      dispatch(loadMore());
    }
  };
  const listingLoader = <ListingProductLoader length={PRODUCT_LOADER_COUNT} />;
  const showLoadMore = isSearch;
  const showInfiniteLoader =
    showLoadMore &&
    !stopFurtherCall &&
    !errorFetchingPage &&
    pageNumber <= INFINITE_SCROLL_PAGE_THRESHOLD;
  const showSpinner = isPageLoading && !showLoadMore;
  const totalPageCount = pageNumber > totalPages ? 0 : 5;
  return (
    <>
      <Scroll
        isSearch={isSearch}
        isPage={listingPage?.isPageChanged}
        isFilter={listingPage?.isFilterChanged}
      />
      {!noProducts && (
        <ProductList
          products={products}
          hasInteracted={hasInteracted}
          setInteraction={setInteraction}
          journeyData={journeyData}
          isInlineWidgetEnabled={!isSearch && inlineWidgetEnabled}
          tagsOnListingEnabled={tagsOnListingEnabled}
          tagsV2Enabled={tagsV2Enabled}
        />
      )}
      {!showLoadMore && (noProducts || stopFurtherCall) && (
        <NoProduct>{NO_PRODUCT_MESSAGE}</NoProduct>
      )}
      {showInfiniteLoader && (
        <InfiniteLoader
          loadMore={loadMoreClick}
          threshold={900}
          isLoading={isPageLoading}
          loader={listingLoader}
        />
      )}
      {!showLoadMore && (
        <PaginationByNumber
          startNumber={1}
          count={totalPageCount}
          totalPages={totalPages}
          changePage={changePage}
          selectedPage={pageNumber}
        />
      )}
      {showSpinner && <Spinner />}
      {showLoadMore && (
        <LoadMore>
          {stopFurtherCall && <p>{NO_PRODUCT_MESSAGE}</p>}
          {!stopFurtherCall && (
            <button
              onClick={loadMoreClick}
              id="load-more"
              type="button"
              className="load-more-button"
            >
              {`${LOAD_MORE_BUTTON_TEXT} ( ${remainingProduct} )`}
            </button>
          )}
        </LoadMore>
      )}
    </>
  );
};

export default View;
